// src/components/ItemModifierModal.tsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { MdClose, MdShoppingCart } from 'react-icons/md';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { motion, AnimatePresence, Variants, useReducedMotion } from 'framer-motion';
import { MenuItemDetails } from '../redux/slices/menuItemDetailsSlice';
import { formatPrice } from '../utils/priceUtils';
import { ModifierGroup } from './modifiers';
import { useModifierValidation } from '../hooks/useModifierValidation';
import { checkItemAvailability } from '../utils/itemAvailability';
import { useAppSelector } from '../redux/hooks';
import { ModifierGroup as ModifierGroupType } from '../types/modifier';
import { ItemModifierModalSkeleton } from './ItemModifierModalSkeleton';
import { calculateMenuItemTotalPrice } from '../utils/priceCalculations';

// Animation variants
const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

interface ItemModifierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCart?: (item: MenuItemDetails, quantity: number, modifiers: any) => Promise<void> | void;
  initialModifiers?: any[];
  initialQuantity?: number;
  editMode?: boolean;
}

export const ItemModifierModal: React.FC<ItemModifierModalProps> = ({
  isOpen,
  onClose,
  onAddToCart,
  initialModifiers,
  initialQuantity,
  editMode = false,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const animationTransition = shouldReduceMotion ? { duration: 0 } : springTransition;

  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get item, modifiers and loading state from Redux (ONLY source of data)
  const { item, modifiers: modifiersList, loading } = useAppSelector((state) => state.menuItemDetails);

  // NEW modifier validation hook
  const {
    selections: modifierSelections,
    isValid: isModifiersValid,
    handleSelectOption,
    handleQuantityChange,
    isOptionSelected,
    getOptionQuantity,
    resetSelections,
    initializeSelections,
  } = useModifierValidation(modifiersList as ModifierGroupType[]);

  // Reset when modal opens - use initial values if in edit mode
  useEffect(() => {
    if (isOpen && item) {
      // Use initial quantity if provided, otherwise default to 1
      setQuantity(initialQuantity || 1);
      
      // Initialize selections from previous modifiers if in edit mode
      // Handle both array and object formats
      const hasInitialModifiers = initialModifiers && 
        (Array.isArray(initialModifiers) ? initialModifiers.length > 0 : Object.keys(initialModifiers).length > 0);
      
      if (editMode && hasInitialModifiers) {
        initializeSelections(initialModifiers);
      } else {
        resetSelections();
      }
      
      setIsAddingToCart(false);
      setError(null);
    }
  }, [isOpen, item, initialQuantity, initialModifiers, editMode, resetSelections, initializeSelections, modifiersList]);

  // ESC key close
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  // Calculate total price using reusable utility
  const totalPrice = useMemo(() => {
    if (!item) return 0;
    
    const basePrice = typeof item.price === 'object' 
      ? item.price.online 
      : parseFloat(String(item.price) || '0');
    
    return calculateMenuItemTotalPrice(
      basePrice,
      modifiersList,
      modifierSelections,
      quantity
    );
  }, [item, modifiersList, modifierSelections, quantity]);

  const handleAddToCartClick = useCallback(async () => {
    if (!item || !isModifiersValid || !onAddToCart) {
      return;
    }

    setIsAddingToCart(true);
    setError(null);

    try {
      await onAddToCart(item, quantity, modifierSelections);
      onClose();
    } catch (err) {
      console.error('Add to cart failed:', err);
      setError('Failed to add item to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  }, [item, quantity, modifierSelections, onAddToCart, onClose, isModifiersValid]);

  // Check item availability using utility function
  const availabilityCheck = checkItemAvailability({
    inventoryStatus: item?.inventoryStatus,
    isItemVerified: item?.isItemVerified,
    isDeleted: item?.isDeleted
  });
  
  const isUnavailable = !availabilityCheck.isAvailable;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-0 sm:p-4"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={animationTransition}
          className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg md:max-w-xl lg:max-w-lg sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-gray-100 px-4 py-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {item?.name || 'Loading...'}
              </h2>
              {/* Show price directly below item name */}
              {item?.price && (
                <p className="text-base font-semibold text-gray-700 mt-1">
                  {formatPrice(typeof item.price === 'object' ? item.price.online : item.price)}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white hover:bg-gray-50 rounded-full shadow-sm transition-colors"
              aria-label="Close modal"
            >
              <MdClose className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Show Loading Skeleton or Content */}
          {loading ? (
            <ItemModifierModalSkeleton />
          ) : (
            <>
              {/* Scrollable Content */}
              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto overscroll-contain"
                style={{ paddingBottom: onAddToCart ? '80px' : '0px' }}
              >
                {/* Content */}
                <div className="p-4 space-y-5">

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-600 text-sm font-medium">{error}</p>
                    </div>
                  )}

                  {/* Modifiers */}
                  {modifiersList && modifiersList.length > 0 && (
                    <div className="space-y-4">
                      {modifiersList.map((group) => (
                        <ModifierGroup
                          key={group.id}
                          group={group}
                          isOptionSelected={(optionId: string) => isOptionSelected(group.id, optionId)}
                          getOptionQuantity={(optionId: string) => getOptionQuantity(group.id, optionId)}
                          onSelectOption={(optionId: string) => handleSelectOption(group.id, optionId)}
                          onQuantityChange={(optionId: string, qty: number) =>
                            handleQuantityChange(group.id, optionId, qty)
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Fixed Footer - Only show when not loading */}
              {onAddToCart && (
                <div
                  className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg"
                  style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
                >
                  <div className="flex items-center gap-3">
                    {/* Quantity Controls */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all disabled:opacity-50"
                        aria-label="Decrease quantity"
                      >
                        <FaMinus className="w-3 h-3 text-gray-600" />
                      </button>
                      <span className="w-10 text-center font-bold text-gray-900">{quantity}</span>
                      <button
                        onClick={() => setQuantity((q) => q + 1)}
                        className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all"
                        aria-label="Increase quantity"
                      >
                        <FaPlus className="w-3 h-3 text-gray-600" />
                      </button>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={handleAddToCartClick}
                      disabled={isAddingToCart || isUnavailable || !isModifiersValid}
                      className={`flex-1 font-semibold py-3.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-between ${
                        isAddingToCart || isUnavailable || !isModifiersValid
                          ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                          : 'text-white shadow-lg hover:shadow-xl'
                      }`}
                      style={{
                        backgroundColor: (isAddingToCart || isUnavailable || !isModifiersValid)
                          ? undefined
                          : '#FA2C37'
                      }}
                    >
                      {isUnavailable ? (
                        <div className="flex items-center justify-center w-full">
                          <MdShoppingCart className="w-5 h-5 mr-2" />
                          {availabilityCheck.message}
                        </div>
                      ) : isAddingToCart ? (
                        <div className="flex items-center justify-center w-full">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                          Adding...
                        </div>
                      ) : (
                        <>
                          <span className="flex items-center">
                            <MdShoppingCart className="w-5 h-5 mr-2" />
                            {editMode ? 'Update to Cart' : 'Add to Cart'}
                          </span>
                          <span className="font-bold">{formatPrice(totalPrice)}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

ItemModifierModal.displayName = 'ItemModifierModal';

export default ItemModifierModal;
